import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FaQuestion, FaRobot } from 'react-icons/fa';
import Navbar from './components/Navbar';
import Shelf from './components/Shelf';
import ProjectCard from './components/ProjectCard';
import ProjectDetails from './components/ProjectDetails';
import HelpModal from './components/HelpModal';
import ChatBot from './components/ChatBot';
import SplashScreen from './components/SplashScreen';
import AuthPage from './components/AuthPage';
import { commonCoreProjects, newCommonCoreProjects } from './data/projects';
import { API_BASE_URL } from './config';
import './App.css';

const Dashboard = ({ projects, title, enableFiltering, headerExtras }) => {
  const [filter, setFilter] = useState('All');

  // If filtering is enabled, get unique ranks (branches) to use as filter options
  const categories = enableFiltering 
    ? ['All', ...new Set(projects.map(p => p.rank))].sort() 
    : [];

  // Filter projects based on selection
  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(p => p.rank === filter);

  // Group projects by Rank (Branch) for display
  const projectsByRank = filteredProjects.reduce((acc, project) => {
    if (!acc[project.rank]) {
      acc[project.rank] = [];
    }
    acc[project.rank].push(project);
    return acc;
  }, {});

  const sortedRankKeys = Object.keys(projectsByRank).sort((a, b) => {
    const aOrder = projectsByRank[a]?.[0]?.rank_order;
    const bOrder = projectsByRank[b]?.[0]?.rank_order;

    if (typeof aOrder === 'number' && typeof bOrder === 'number') {
      return aOrder - bOrder;
    }
    if (typeof aOrder === 'number') return -1;
    if (typeof bOrder === 'number') return 1;
    return a.localeCompare(b);
  });

  return (
    <>
      <div className="hero-section">
        <h1>{title}</h1>
        <p>Access peer reviews, resources, and community feedback.</p>

        {headerExtras}
        
        {enableFiltering && (
          <div className="category-filter">
            {categories.map(cat => (
              <button 
                key={cat} 
                className={`filter-btn ${filter === cat ? 'active' : ''}`}
                onClick={() => setFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      {sortedRankKeys.map((rank) => (
        <Shelf key={rank} title={rank}>
          {projectsByRank[rank].map((proj, idx) => (
            <ProjectCard key={idx} {...proj} />
          ))}
        </Shelf>
      ))}
    </>
  );
};



function App() {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  React.useEffect(() => {
    const checkAuth = async () => {
      // 1. Check if token is in URL (from 42 callback) or LocalStorage
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get('token');
      const storedToken = localStorage.getItem('authToken');
      
      const tokenToCheck = urlToken || storedToken;

      if (!tokenToCheck) {
        setIsLoadingAuth(false);
        return;
      }

      // Demo mode: bypass backend entirely
      if (tokenToCheck === 'demo') {
        setUser({ login: 'Demo', image_url: null });
        setIsAuthenticated(true);
        setIsLoadingAuth(false);
        return;
      }

      // If we found a token in URL, save it
      if (urlToken) {
          localStorage.setItem('authToken', urlToken);
          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
      }

      try {
        const res = await fetch(`${API_BASE_URL}/auth/me`, {
           method: 'GET',
           credentials: 'include', // Ensure cookies are sent
           headers: {
             'Authorization': `Bearer ${tokenToCheck}`,
             'Content-Type': 'application/json'
           }
        });
        
        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
          setIsAuthenticated(true);
        } else {
            console.warn('Backend rejected token. Status:', res.status);
            // FALLBACK FOR DEMO/DEV if Backend returns 401/CORS error
            // Check if we have a token, if so, assume it's valid for now to bypass backend issues
            if (tokenToCheck) {
                 console.log("Using Fallback Auth (Backend Error)");
                 setUser({ login: 'Guest', image_url: null }); 
                 setIsAuthenticated(true);
            }
        }
      } catch (error) {
        console.error("Auth check failed (Network/CORS):", error);
        // FALLBACK FOR NETWORK FAILURES
        if (tokenToCheck) {
             console.log("Using Fallback Auth (Network Error)");
             setUser({ login: 'Guest', image_url: null }); 
             setIsAuthenticated(true);
        }
      } finally {
        setIsLoadingAuth(false);
      }
    };

    checkAuth();
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 2500); // 2s delay + 0.5s fade
    return () => clearTimeout(timer);
  }, []);

  const handleLogout = async () => {
    // Clear local storage immediately
    localStorage.removeItem('authToken');
    
    try {
      // Optional: Notify backend
      await fetch(`${API_BASE_URL}/auth/logout`, { 
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      console.error("Logout failed", err);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  if (isLoadingAuth || showSplash) {
     return <SplashScreen />;
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  const CommonCoreLibrary = () => {
    const [coreTrack, setCoreTrack] = React.useState(() => {
      return localStorage.getItem('commonCoreTrack') || 'old';
    });

    React.useEffect(() => {
      localStorage.setItem('commonCoreTrack', coreTrack);
    }, [coreTrack]);

    const projects = coreTrack === 'new' ? newCommonCoreProjects : commonCoreProjects;

    return (
      <Dashboard
        projects={projects}
        title={coreTrack === 'new' ? 'New Common Core' : 'Old Common Core'}
        enableFiltering={false}
        headerExtras={(
          <div className="category-filter" style={{ marginTop: '2rem' }}>
            <button
              className={`filter-btn ${coreTrack === 'old' ? 'active' : ''}`}
              onClick={() => setCoreTrack('old')}
            >
              Old Common Core
            </button>
            <button
              className={`filter-btn ${coreTrack === 'new' ? 'active' : ''}`}
              onClick={() => setCoreTrack('new')}
            >
              New Common Core
            </button>
          </div>
        )}
      />
    );
  };

  return (
    <Router>
      <div className="App">
        <Helmet>
          <title>MintraLib</title>
          <meta name="description" content="MintraLib (Mintra) - The ultimate resource for 42 School students." />
        </Helmet>
        {/* Background Animation Elements */}
        <div className="app-background">
          <div className="bg-shape shape-1"></div>
          <div className="bg-shape shape-2"></div>
          <div className="bg-shape shape-3"></div>
        </div>
        <Navbar onLogout={handleLogout} user={user} />
        <div className="main-container">
          
          <main className="content-area">
            <Routes>
              <Route path="/" element={<Navigate to="/common-core" />} />
              <Route path="/common-core" element={<CommonCoreLibrary />} />
              <Route path="/project/:projectId" element={<ProjectDetails user={user} />} />
            </Routes>
          </main>

          <div className="fab-container">
            {!isChatOpen && (
            <button 
              type="button"
              className="help-fab chatbot-fab" 
              onClick={() => setIsChatOpen(true)}
              aria-label="AI Assistant"
            >
              <FaRobot />
            </button>
            )}
            <button 
              type="button"
              className="help-fab" 
              onClick={() => setIsHelpOpen(true)} 
              aria-label="What is Mintra?"
            >
              <FaQuestion />
            </button>
          </div>
          
          <ChatBot isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
          <HelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />

        </div>
      </div>
    </Router>
  );
}


export default App;


